/**
 * Joining age, and the tier a birth date implies.
 *
 * Owner's direction, 16 Aug 2026. Two rules, deliberately narrow:
 *
 *   under 12  — cannot join at all
 *   12 to 17  — junior
 *   18 and up — annual
 *
 * Student and life are NEVER inferred. Being a student is not something a
 * birth date can prove, and life is a payment decision, not an age bracket —
 * guessing either would quietly charge the wrong fee to a real person. The
 * form defaults from age and the admin can change it afterwards, which is the
 * only place that judgement belongs.
 */

import { ageFrom } from './time';

/** Below this, the club does not accept a member. */
export const MIN_JOIN_AGE = 12;

/** Older than this is a mistyped year, not a runner. */
const MAX_PLAUSIBLE_AGE = 110;

/**
 * The tier an age implies, or null when it implies nothing.
 *
 * Null means "do not change what the admin picked" — an unknown age must not
 * silently rewrite a tier the admin chose on purpose.
 */
export function tierForAge(age) {
  if (age === null || age === undefined || Number.isNaN(age)) return null;
  if (age < MIN_JOIN_AGE) return null;
  return age < 18 ? 'junior' : 'annual';
}

/** Same, straight from an ISO date string. */
export function tierForDob(dob) {
  return tierForAge(ageFrom(dob || null));
}

/** Oldest a member can be and still be offered the student tier. */
export const STUDENT_MAX_AGE = 25;

/**
 * The tiers a member of this age may be OFFERED, in TIERS order.
 *
 * Owner's direction, 17 Aug 2026: the add-member form must not show junior
 * to an adult, nor student to anyone past {@link STUDENT_MAX_AGE} — both were
 * selectable for a 40-year-old, and each one is a ₹750 discount handed out by
 * a mis-click. An unknown age filters nothing: the admin may know something
 * the form does not, and hiding options on no evidence would only get in the
 * way.
 */
export function allowedTiersForAge(age) {
  const all = ['annual', 'student', 'junior', 'life'];
  if (age === null || age === undefined || Number.isNaN(age)) return all;
  return all.filter((tier) => {
    if (tier === 'junior' && age >= 18) return false;
    if (tier === 'student' && age > STUDENT_MAX_AGE) return false;
    return true;
  });
}

/**
 * Why this date of birth is not acceptable, or null if it is.
 *
 * A blank date is allowed: date of birth is optional on the form, and a
 * missing one is honest. A date in the FUTURE is not — it is a typo every
 * time, and one slipped through before this existed (a member was saved with
 * a 2025 birth date, which the tier logic would have read as an infant).
 */
export function ageError(dob) {
  if (!dob) return null;

  const age = ageFrom(dob);
  if (age === null) return 'That date of birth is not a valid date.';

  if (age < 0) {
    return 'That date of birth is in the future — please check it.';
  }
  if (age > MAX_PLAUSIBLE_AGE) {
    return 'That date of birth looks wrong — please check the year.';
  }
  if (age < MIN_JOIN_AGE) {
    return `Members must be at least ${MIN_JOIN_AGE} years old. This date of birth gives an age of ${age}.`;
  }
  return null;
}
